import { useState, useEffect } from 'react'
import { setupAPI } from '../services/api'

export interface SetupStatus {
  isComplete: boolean
  isChecking: boolean
  error: string | null
  databaseStats: {
    users: number
    games: number
    categories: number
    tags: number
    isHealthy: boolean
  } | null
}

export const useSetupStatus = () => {
  const [status, setStatus] = useState<SetupStatus>({
    isComplete: false,
    isChecking: true,
    error: null,
    databaseStats: null
  })

  const checkSetupStatus = async () => {
    try {
      setStatus(prev => ({ ...prev, isChecking: true, error: null }))

      // Always check database first (source of truth)
      const response = await setupAPI.getDatabaseStats()
      
      if (!response?.data) {
        throw new Error('No data received from database stats')
      }
      
      const stats = response.data
      
      setStatus(prev => ({
        ...prev,
        databaseStats: stats
      }))

      // Database has data = setup is complete
      if (stats.users > 0) {
        // Sync localStorage with database reality
        localStorage.setItem('system-setup-complete', 'true')
        localStorage.setItem('setup-completed-at', new Date().toISOString())
        
        setStatus(prev => ({
          ...prev,
          isComplete: true,
          isChecking: false
        }))
      } else {
        // Database is empty = setup needed
        // Clear any stale localStorage flags
        localStorage.removeItem('system-setup-complete')
        localStorage.removeItem('setup-completed-at')
        
        setStatus(prev => ({
          ...prev,
          isComplete: false,
          isChecking: false
        }))
      }
    } catch (error: any) {
      console.error('Error checking setup status:', error)
      
      // If can't reach database, fall back to localStorage
      const setupComplete = localStorage.getItem('system-setup-complete')
      
      setStatus(prev => ({
        ...prev,
        isComplete: setupComplete === 'true',
        isChecking: false,
        error: error.message || 'Failed to check setup status'
      }))
    }
  }

  const markSetupComplete = () => {
    localStorage.setItem('system-setup-complete', 'true')
    localStorage.setItem('setup-completed-at', new Date().toISOString())
    setStatus(prev => ({
      ...prev,
      isComplete: true
    }))
  }

  const resetSetup = () => {
    localStorage.removeItem('system-setup-complete')
    localStorage.removeItem('setup-completed-at')
    setStatus(prev => ({
      ...prev,
      isComplete: false
    }))
  }

  useEffect(() => {
    checkSetupStatus()
  }, [])

  return {
    ...status,
    checkSetupStatus,
    markSetupComplete,
    resetSetup
  }
}
